import { useScrollAnimation } from "@/hooks/useScrollAnimation";

const SPOTIFY_APP = "spotify:show:1YrHOAOWCzoowFEcrh6wUm";
const SPOTIFY_WEB = "https://open.spotify.com/show/1YrHOAOWCzoowFEcrh6wUm";
const APPLE_APP = "podcast://podcasts.apple.com/us/podcast/midwest-fear-fest-behind-the-screams/id6808319109";
const APPLE_WEB = "https://podcasts.apple.com/us/podcast/midwest-fear-fest-behind-the-screams/id6808319109";

function openAppWithFallback(appUrl: string, webUrl: string) {
  // If the app opens, the window blurs — use that to cancel the web fallback
  const fallback = setTimeout(() => window.open(webUrl, "_blank"), 1500);
  window.addEventListener("blur", () => clearTimeout(fallback), { once: true });
  window.location.href = appUrl;
}

export default function Schedule() {
  const ref = useScrollAnimation();

  return (
    <section
      id="podcast"
      style={{
        background: "#0f0f14",
        padding: "clamp(64px,10vw,120px) clamp(20px,5vw,80px)",
      }}
    >
      <div
        ref={ref}
        style={{ maxWidth: "800px", margin: "0 auto", textAlign: "center" }}
      >
        {/* Header */}
        <div style={{ marginBottom: "40px" }}>
          <div
            className="section-label animate-on-scroll"
            style={{ justifyContent: "center", marginBottom: "16px" }}
          >
            Beyond the Festival
          </div>
          <h2
            className="display-heading animate-on-scroll"
            style={{
              fontSize: "clamp(2.5rem,6vw,4.5rem)",
              color: "#e8e8e8",
              transitionDelay: "100ms",
            }}
          >
            Behind the <span style={{ color: "#cc0000" }}>Screams</span>
          </h2>
        </div>

        <p
          className="animate-on-scroll"
          style={{
            color: "rgba(232,232,232,0.7)",
            fontSize: "1.05rem",
            lineHeight: 1.85,
            margin: "0 auto 48px",
            transitionDelay: "150ms",
          }}
        >
          At Midwest Fear Fest, we believe in spotlighting filmmakers long after
          the credits roll. That's why we launched our companion podcast,{" "}
          <em>Midwest Fear Fest: Behind the Screams</em>. The podcast gives
          festival alumni a platform to share behind-the-scenes stories from
          their shorts, answer burning questions from festival attendees, and
          share what's next in their filmmaking journeys.
        </p>

        {/* Platform buttons */}
        <div
          className="animate-on-scroll"
          style={{
            display: "flex",
            gap: "16px",
            justifyContent: "center",
            flexWrap: "wrap",
            transitionDelay: "200ms",
          }}
        >
          <a
            href={SPOTIFY_WEB}
            onClick={(e) => { e.preventDefault(); openAppWithFallback(SPOTIFY_APP, SPOTIFY_WEB); }}
            rel="noopener noreferrer"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              padding: "14px 32px",
              borderRadius: "999px",
              background: "#1DB954",
              color: "#fff",
              fontFamily: '"Bebas Neue", cursive',
              fontSize: "0.95rem",
              letterSpacing: "0.12em",
              textDecoration: "none",
              transition: "opacity 0.2s ease",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.82")}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z" />
            </svg>
            Listen on Spotify
          </a>

          <a
            href={APPLE_WEB}
            onClick={(e) => { e.preventDefault(); openAppWithFallback(APPLE_APP, APPLE_WEB); }}
            rel="noopener noreferrer"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              padding: "14px 32px",
              borderRadius: "999px",
              background: "#872EC4",
              color: "#fff",
              fontFamily: '"Bebas Neue", cursive',
              fontSize: "0.95rem",
              letterSpacing: "0.12em",
              textDecoration: "none",
              transition: "opacity 0.2s ease",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.82")}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M12 0C5.376 0 0 5.376 0 12s5.376 12 12 12 12-5.376 12-12S18.624 0 12 0zm.48 4.32c3.744 0 6.72 2.976 6.72 6.72 0 .624-.096 1.248-.24 1.824l-1.44-.384c.096-.48.144-.96.144-1.44 0-2.88-2.304-5.184-5.184-5.184s-5.184 2.304-5.184 5.184c0 2.208 1.392 4.128 3.36 4.896v1.536C7.68 16.272 5.76 13.872 5.76 11.04c0-3.744 2.976-6.72 6.72-6.72zm0 2.88c2.112 0 3.84 1.728 3.84 3.84 0 .336-.048.672-.144.96l-1.44-.384c.048-.192.048-.384.048-.576 0-1.248-1.008-2.304-2.304-2.304s-2.304 1.056-2.304 2.304c0 .912.528 1.728 1.296 2.16v1.584c-1.584-.528-2.736-2.016-2.736-3.744 0-2.112 1.728-3.84 3.744-3.84zM12 9.6a1.44 1.44 0 110 2.88A1.44 1.44 0 0112 9.6zm-.48 3.792v6.048a7.93 7.93 0 01-5.856-5.232l1.44-.384a6.458 6.458 0 004.416 4.272v-4.32l-.816-2.016 1.392-.576.816 2.016-.816.192h-.576z" />
            </svg>
            Listen on Apple
          </a>
        </div>
      </div>
    </section>
  );
}
